import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Box, FormControlLabel, Switch, Tab, Tabs, Typography } from '@mui/material'
import * as actions from '../../Redux/actions'
import ChatTranscript from '../ChatTranscript/ChatTranscript'
import FloatingSimulator from '../FloatingSimulator/FloatingSimulator'
import FlowsList from '../FlowsList/FlowsList'
import SessionsList from '../SessionsList/SessionsList'
import { USSDSTART_FLOW_REF, LIVE_POLL_INTERVAL_MS } from '../../constants'

function TabPanel({ value, index, children }) {
  if (value !== index) return null
  return <Box sx={{ paddingTop: 2 }}>{children}</Box>
}

// Embedded directly on a customer's own profile (Customer Status > USSD Sessions tab) - unlike the
// standalone /admin/ussd-sessions page, the phone number is already known from the customer record, so
// there's no search step here, just an msisdn prop and an automatic fetch on mount/change.
function CustomerUssdSessionsPanel(props) {
  const { msisdn } = props
  const [tab, setTab] = useState(0)
  const [live, setLive] = useState(false)

  useEffect(() => {
    if (!msisdn) return
    props.dispatch(actions.getUssdSessionsRequest(msisdn))
    props.dispatch(actions.getUssdMessagesRequest(msisdn))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msisdn])

  // A customer's session can be actively progressing (or a new one can start) while a business user is
  // sitting on this tab - opt-in polling so they see it without needing to refresh the page, without
  // hitting mailroom on every profile view whether anyone's watching or not. No push mechanism
  // (SSE/websocket) exists on mailroom's business API today, and this view is low-traffic/support-facing,
  // so plain polling is the right-sized fix rather than building one.
  useEffect(() => {
    if (!msisdn || !live) return
    const interval = setInterval(() => {
      props.dispatch(actions.getUssdSessionsRequest(msisdn))
      props.dispatch(actions.getUssdMessagesRequest(msisdn))
    }, LIVE_POLL_INTERVAL_MS)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msisdn, live])

  // Flows aren't scoped to a customer - fetched once regardless of which msisdn this panel is showing.
  useEffect(() => {
    props.dispatch(actions.getUssdFlowsRequest())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!msisdn) {
    return (
      <Typography variant="body2">
        No phone number on file for this customer.
      </Typography>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)}>
          <Tab label="Sessions" />
          <Tab label="Flows" />
          <Tab label="Chat" />
        </Tabs>
        <FormControlLabel
          control={<Switch checked={live} onChange={e => setLive(e.target.checked)} size="small" />}
          label="Live"
        />
      </Box>

      <TabPanel value={tab} index={0}>
        <SessionsList
          sessions={props.ussdSessions.ussd_sessions}
          sessionsLoading={props.ussdSessions.sessions_loader && !props.ussdSessions.ussd_sessions}
          messages={props.ussdSessions.ussd_messages}
          messagesLoading={props.ussdSessions.messages_loader && !props.ussdSessions.ussd_messages}
        />
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <FlowsList
          flows={props.ussdSessions.ussd_flows}
          flowsLoading={props.ussdSessions.flows_loader}
        />
      </TabPanel>

      <TabPanel value={tab} index={2}>
        <ChatTranscript
          messages={props.ussdSessions.ussd_messages}
          loading={props.ussdSessions.messages_loader && !props.ussdSessions.ussd_messages}
          sessions={props.ussdSessions.ussd_sessions}
        />
      </TabPanel>

      <FloatingSimulator flowUuid={USSDSTART_FLOW_REF} msisdn={msisdn} />
    </Box>
  )
}

export default connect(state => ({
  ussdSessions: state.ussdSessions
}))(CustomerUssdSessionsPanel)
